/**
 * StaffMemoPage — 총무 메모 / 전달사항
 *
 * 다음 근무자에게 남기는 인수인계 게시판.
 * 확인 버튼을 누르면 그 즉시 누가·언제 봤는지 남고, 처리 상태는 확인 대기 → 처리중 → 처리완료.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import './StaffMemoPage.css';

type Priority = 'normal' | 'urgent';
type MemoStatus = 'pending' | 'in_progress' | 'done';
type Scope = 'all' | 'mine' | 'pending' | 'in_progress' | 'done';

interface MemoRead {
  user_name: string;
  read_at: string | null;
}

interface StaffMemo {
  id: string;
  author_name: string;
  target_name: string;
  is_broadcast: boolean;
  is_mine: boolean;
  is_for_me: boolean;
  priority: Priority;
  priority_label: string;
  content: string;
  status: MemoStatus;
  status_label: string;
  created_at: string | null;
  resolved_at: string | null;
  resolved_by_name: string;
  reads: MemoRead[];
  read_by_me: boolean;
}

interface Counts {
  all: number;
  mine: number;
  pending: number;
  in_progress: number;
  done: number;
}

interface Recipient {
  id: string;
  name: string;
}

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'mine', label: '나에게' },
  { value: 'pending', label: '확인 대기' },
  { value: 'in_progress', label: '처리중' },
  { value: 'done', label: '처리완료' },
];

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const PAGE_SIZE = 20;

const StaffMemoPage: React.FC = () => {
  const [memos, setMemos] = useState<StaffMemo[]>([]);
  const [counts, setCounts] = useState<Counts>({ all: 0, mine: 0, pending: 0, in_progress: 0, done: 0 });
  const [hasMore, setHasMore] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [scope, setScope] = useState<Scope>('all');
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState<Priority>('normal');
  const [targetId, setTargetId] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const apiCall = useCallback(async (path: string, options: RequestInit = {}) => {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/api/v1/owner/staff-memos${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(typeof json.detail === 'string' ? json.detail : '요청을 처리하지 못했습니다.');
    }
    return json;
  }, []);

  const load = useCallback(async () => {
    try {
      setError('');
      const json = await apiCall(`?scope=${scope}&limit=${limit}&offset=0`);
      setMemos(json.data?.items || []);
      setCounts(json.data?.counts || { all: 0, mine: 0, pending: 0, in_progress: 0, done: 0 });
      setHasMore(!!json.data?.has_more);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, scope, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    apiCall('/recipients')
      .then((json) => setRecipients(json.data || []))
      .catch(() => setRecipients([]));
  }, [apiCall]);

  const handleScope = (value: Scope) => {
    setScope(value);
    setLimit(PAGE_SIZE);
  };

  const handleCreate = async () => {
    if (!content.trim()) {
      setError('전달할 내용을 적어주세요.');
      return;
    }
    try {
      await apiCall('', {
        method: 'POST',
        body: JSON.stringify({
          content: content.trim(),
          priority,
          target_user_id: targetId || null,
        }),
      });
      setContent('');
      setPriority('normal');
      setTargetId('');
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleRead = async (memo: StaffMemo) => {
    if (busyId) return;
    setBusyId(memo.id);
    try {
      await apiCall(`/${memo.id}/read`, { method: 'POST' });
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleStatus = async (memo: StaffMemo, status: MemoStatus) => {
    if (busyId) return;
    setBusyId(memo.id);
    try {
      await apiCall(`/${memo.id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (memo: StaffMemo) => {
    setBusyId(memo.id);
    try {
      await apiCall(`/${memo.id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="staff-memo">
      <div className="smo-header">
        <div>
          <h1>메모 / 전달사항</h1>
          <p className="smo-sub">
            다음 근무자에게 남기는 전달사항입니다. 확인 버튼을 누르면 누가 언제 봤는지 기록에 남습니다.
          </p>
        </div>
        <button className="smo-btn primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? '취소' : '+ 새 메모 작성'}
        </button>
      </div>

      {error && <div className="smo-alert error">{error}</div>}

      {showForm && (
        <div className="smo-form">
          <div className="smo-form-row">
            <label>
              중요도
              <select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                <option value="normal">일반</option>
                <option value="urgent">긴급</option>
              </select>
            </label>
            <label className="smo-target">
              받는 사람
              <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                <option value="">전체 총무</option>
                {recipients.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <textarea
            className="smo-textarea"
            rows={3}
            placeholder="예) 3층 여자 열람실 에어컨이 약하게 나와요. 확인 후 조치 부탁드려요."
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="smo-form-actions">
            <button className="smo-btn primary" onClick={handleCreate}>
              등록
            </button>
            <button className="smo-btn" onClick={() => setShowForm(false)}>
              취소
            </button>
          </div>
        </div>
      )}

      <div className="smo-tabs">
        {SCOPES.map((s) => (
          <button
            key={s.value}
            className={`smo-tab ${scope === s.value ? 'active' : ''}`}
            onClick={() => handleScope(s.value)}
          >
            {s.label} <span>{counts[s.value]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="smo-empty">불러오는 중…</div>
      ) : memos.length === 0 ? (
        <div className="smo-empty-box">
          <p>아직 남겨진 전달사항이 없습니다.</p>
          <span className="smo-hint">
            다음 근무자가 알아야 할 일이 있으면 「+ 새 메모 작성」으로 남겨주세요.
          </span>
        </div>
      ) : (
        <div className="smo-list">
          {memos.map((memo) => (
            <div
              key={memo.id}
              className={`smo-item ${memo.priority === 'urgent' && memo.status !== 'done' ? 'urgent' : ''} ${
                memo.status === 'done' ? 'done' : ''
              }`}
            >
              <div className="smo-item-top">
                <span className={`smo-badge ${memo.priority}`}>{memo.priority_label}</span>
                <span className="smo-who">
                  {memo.author_name || '작성자'} <span className="smo-arrow">→</span>{' '}
                  {memo.is_broadcast ? '전체 총무' : `${memo.target_name} 총무`}
                </span>
                <span className="smo-time">{memo.created_at}</span>
              </div>

              <div className="smo-content">{memo.content}</div>

              <div className="smo-item-bottom">
                <div className="smo-reads">
                  {memo.reads.length === 0 ? (
                    <span className="smo-noread">아직 확인한 사람이 없습니다</span>
                  ) : (
                    memo.reads.map((r, i) => (
                      <span key={i} className="smo-read">
                        👁 {r.user_name} 확인 {r.read_at}
                      </span>
                    ))
                  )}
                  {memo.status === 'done' && memo.resolved_by_name && (
                    <span className="smo-read done">
                      ✓ {memo.resolved_by_name} 처리완료 {memo.resolved_at}
                    </span>
                  )}
                </div>

                <div className="smo-actions">
                  {!memo.read_by_me && (
                    <button
                      className="smo-btn warn"
                      disabled={busyId === memo.id}
                      onClick={() => handleRead(memo)}
                    >
                      확인했습니다
                    </button>
                  )}
                  {memo.status === 'pending' && (
                    <button
                      className="smo-btn"
                      disabled={busyId === memo.id}
                      onClick={() => handleStatus(memo, 'in_progress')}
                    >
                      처리하기
                    </button>
                  )}
                  {memo.status === 'in_progress' && (
                    <button
                      className="smo-btn info"
                      disabled={busyId === memo.id}
                      onClick={() => handleStatus(memo, 'done')}
                    >
                      처리중 → 완료
                    </button>
                  )}
                  {memo.status === 'done' && (
                    <button
                      className="smo-btn ok"
                      disabled={busyId === memo.id}
                      onClick={() => handleStatus(memo, 'in_progress')}
                    >
                      ✓ 처리완료
                    </button>
                  )}
                  {confirmDeleteId === memo.id ? (
                    <>
                      <button
                        className="smo-btn danger"
                        disabled={busyId === memo.id}
                        onClick={() => handleDelete(memo)}
                      >
                        삭제 확인
                      </button>
                      <button className="smo-btn" onClick={() => setConfirmDeleteId(null)}>
                        취소
                      </button>
                    </>
                  ) : (
                    <button className="smo-btn link" onClick={() => setConfirmDeleteId(memo.id)}>
                      삭제
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {hasMore && (
            <button className="smo-more" onClick={() => setLimit((v) => v + PAGE_SIZE)}>
              더보기 ∨
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffMemoPage;
